import { LEVEL_BANDS, type LevelBand } from '@/shared/levels'

/**
 * "No sé mi nivel" — three questions that place a player on the band scale.
 *
 * WHY THIS EXISTS. Onboarding asks two things, deliberately (2026-08-07: seven
 * questions became two). A mandatory quiz would undo that. This is the opt-in
 * escape hatch instead: a confident player taps their band and never sees it;
 * a player with no idea taps "No sé mi nivel" and answers three questions.
 *
 * The cost of guessing wrong is not cosmetic. The feed PRE-FILTERS on the
 * band, so a player who guesses two levels high sees matches she cannot hold,
 * and one who guesses low never sees the games she actually wants. A persona
 * review put it plainly: *"anchors the ends of the scale but not the middle —
 * she still can't tell if she's a 3 or a 4."*
 *
 * WHY THESE THREE QUESTIONS. Years played is the obvious axis and the wrong
 * one: someone who has played socially for six years can sit below someone
 * who trained for one. Padel has two real technical gates, and players know
 * which side of them they are on without being told:
 *
 *   1. THE BACK WALL. Below `medio` the wall beats you; at `medio` you use it
 *      without thinking. This single question separates the bottom half of the
 *      scale from the top.
 *   2. THE BANDEJA. The shoulder-height defensive smash that keeps you at the
 *      net. No bandeja means you get pushed back every point — it is the gate
 *      into `medio_alto`.
 *   3. COMPETITION. Only distinguishes the top two bands, and only when the
 *      first two answers already put them there.
 *
 * Scoring is a plain sum with no hidden weights, so the result is explainable
 * — and we SHOW the player which band they landed in and why, rather than
 * silently assigning it. They can always override.
 */

export interface QuizOption {
  id: string
  label: string
  /** Points toward the final band. */
  score: number
}

export interface QuizQuestion {
  id: 'wall' | 'bandeja' | 'compete'
  prompt: string
  options: readonly QuizOption[]
}

export const LEVEL_QUIZ: readonly QuizQuestion[] = [
  {
    id: 'wall',
    prompt: '¿Qué pasa cuando la bola te va a la pared?',
    options: [
      { id: 'never', label: 'La dejo. Todavía no la saco', score: 0 },
      { id: 'sometimes', label: 'La saco a veces, sin colocarla', score: 2 },
      { id: 'always', label: 'La saco casi siempre y sé dónde la pongo', score: 4 },
    ],
  },
  {
    id: 'bandeja',
    prompt: '¿Y la bandeja?',
    options: [
      { id: 'no', label: 'No sé lo que es, o no me sale', score: 0 },
      { id: 'basic', label: 'La hago para no perder la red', score: 2 },
      { id: 'placed', label: 'La coloco donde quiero, cruzada o paralela', score: 4 },
    ],
  },
  {
    id: 'compete',
    prompt: '¿Juegas partidos en serio?',
    options: [
      { id: 'social', label: 'Con amigos, por diversión', score: 0 },
      { id: 'regular', label: 'Cada semana, y me gusta ganar', score: 1 },
      { id: 'league', label: 'Liga o torneos federados', score: 3 },
    ],
  },
] as const

export type QuizAnswers = Partial<Record<QuizQuestion['id'], string>>

function bandByKey(key: LevelBand['key']): LevelBand {
  const b = LEVEL_BANDS.find((x) => x.key === key)
  // The keys are a closed union declared alongside the array — unreachable.
  if (!b) throw new Error(`unknown band ${key}`)
  return b
}

/**
 * Total score → band. Max is 11 (4 + 4 + 3).
 *
 * The thresholds are deliberately generous at the bottom and strict at the
 * top: placing somebody one band LOW costs them a few matches they could have
 * played, while placing them one band HIGH drops them into games where they
 * get taken apart — and that is the experience that makes a new player leave.
 * When the answer is borderline we round down.
 */
export function bandFromQuiz(answers: QuizAnswers): LevelBand | null {
  const total = LEVEL_QUIZ.reduce((sum, q) => {
    const picked = q.options.find((o) => o.id === answers[q.id])
    return picked ? sum + picked.score : sum
  }, 0)
  // Every question must be answered — a partial quiz would score low and
  // silently under-place the player.
  const complete = LEVEL_QUIZ.every((q) => answers[q.id] != null)
  if (!complete) return null

  if (total <= 1) return bandByKey('iniciacion')
  if (total <= 3) return bandByKey('medio_bajo')
  if (total <= 6) return bandByKey('medio')
  if (total <= 8) return bandByKey('medio_alto')
  if (total <= 10) return bandByKey('avanzado')
  return bandByKey('competicion')
}
